import React, { createRef, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { BodyCopy } from 'secondstep-components'
import { ReportsClassHeader } from 'components/admin/ClassDrawerHeader'
import DrawerComponent from 'components/DrawerComponent'
import K8Class from 'pages/K8Class'
import NavigationHelper from 'layers/navigation/navigationHelper'
import ReportTableV2 from 'components/ReportTableV2'
import ReportsLoading from 'components/ReportsLoading'
import ReportsPageHeader from 'components/ReportsPageHeader'
import { UNIT_5 } from 'pages/K8Class/constants'
import { K8_REPORT_UNIT_SECTION } from 'pages/K8Report/flag.on/constants'
import { formatLastUpdatedRelativeTime } from 'utils/reportsHelpers'
import { DIGITAL_WELL_BEING_UNIT_LABEL, UNIT_RANGE_OPTIONS } from './selectors'
import {
  ContentWrapper,
  Footer,
  FooterTableAlignCard,
  FooterTableAlignSection,
  ReportTableV2Section,
  PageWrapper,
  ReportWrapper,
} from './component.styles'
import {
  RECENT_ACTIVITY_OPTIONS,
  rowMatchesActivitySelection,
} from './activityFilterConstants'
import {
  K12_SCHOOL_SUMMARY_GRADES,
  K8_SCHOOL_SUMMARY_GRADES,
} from './gradeFilterConstants'
import { summarizeReportingClasses } from './gradeTableSummary'

const SchoolSummary = ({
  administratorsCount,
  classDetailsReport,
  entry,
  handleUnitRangeChange,
  hasClassesWithCompletedLessonLastPeriod,
  isFetchingSchool,
  k8ReportUnitSection,
  lastUpdatedAt,
  navigationHandler,
  numClassesWithCompletedLessonLastPeriod,
  period,
  reportTableData,
  resetFilters,
  selectedActivityKeys,
  selectedClassInstance,
  selectedGradeNames,
  selectedSite,
  selectedUnitRange,
  siteName,
  teachersCount,
  toggleActivityFilter,
  toggleGradeFilter,
  totalClassesCount,
}) => {
  const {
    classLessonColTooltip,
    classMarkedDoneTooltip,
    classStatusColTooltip,
    gradeHeaderTooltip,
    schoolFooterText: footerText,
  } = entry || {}
  const closeButtonRef = createRef(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedClassInstance)
  const [fixedView, setFixedView] = useState(false)
  const [fixedTop, setFixedTop] = useState(false)

  useEffect(() => {
    setIsDrawerOpen(!!selectedClassInstance)
  }, [selectedClassInstance])

  useEffect(() => {
    if (isDrawerOpen && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [isDrawerOpen])

  const handleDrawerScroll = e => {
    const offsetTop = e.target.scrollTop
    if (offsetTop > 22) setFixedTop(true)
    else if (fixedTop) setFixedTop(false)
    if (offsetTop > 52) setFixedView(true)
    else if (fixedView) setFixedView(false)
  }

  const isSpecializedTab =
    k8ReportUnitSection === K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS
  const isBHUSelected =
    !isSpecializedTab && selectedUnitRange === UNIT_RANGE_OPTIONS.BHU

  const selectedClassFromTable = reportTableData?.classes?.find(
    cls => cls.classInstance === selectedClassInstance,
  )

  const coreLessons = classDetailsReport?.lessons?.filter(
    lesson => lesson.parent !== UNIT_5,
  )
  const bhuLessonsList =
    classDetailsReport?.lessons?.filter(lesson => lesson.parent === UNIT_5) ||
    []

  const handleRowClick = (event, row) => {
    if (event.type === 'click' || event.key === 'Enter') {
      event.preventDefault()
      const classInstance = row?.classInstance
      navigationHandler(
        { siteId: selectedSite, classInstance },
        NavigationHelper.types.REPLACE,
      )
    }
  }

  const handleCloseDrawer = event => {
    if (
      event.type === 'click' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      event.preventDefault()
      setIsDrawerOpen(false)
      navigationHandler(
        { siteId: selectedSite },
        NavigationHelper.types.REPLACE,
      )
    }
  }

  const filteredTablePayload = useMemo(() => {
    const gradeFilterProps = {
      activityFilterOptions: RECENT_ACTIVITY_OPTIONS,
      gradeFilterGrades: isSpecializedTab
        ? K12_SCHOOL_SUMMARY_GRADES
        : K8_SCHOOL_SUMMARY_GRADES,
      onActivityFilterToggle: toggleActivityFilter,
      onGradeFilterToggle: toggleGradeFilter,
      onResetFilters: resetFilters,
      selectedActivityKeys,
      selectedGradeNames,
    }

    if (!reportTableData) {
      return { classes: [], showGradeFilter: false, ...gradeFilterProps }
    }

    const sourceClasses = reportTableData.classes ?? []
    const gradeFiltered =
      selectedGradeNames.size === 0
        ? sourceClasses
        : sourceClasses.filter(row => selectedGradeNames.has(row.gradeName))

    const filteredClasses = gradeFiltered.filter(row =>
      rowMatchesActivitySelection(row, selectedActivityKeys),
    )
    const summary = summarizeReportingClasses(filteredClasses)
    const lastUpdatedText =
      lastUpdatedAt && formatLastUpdatedRelativeTime(lastUpdatedAt)
    return {
      ...reportTableData,
      classes: filteredClasses,
      lastUpdatedText,
      totalClassesCount: summary.totalClassesCount,
      numClassesMarkedLastPeriod: summary.numClassesMarkedLastPeriod,
      numClassesNotMarkedLesson: summary.numClassesNotMarkedLesson,
      numOfUnfilteredClasses: sourceClasses.length,
      showGradeFilter: false,
      ...gradeFilterProps,
    }
  }, [
    isSpecializedTab,
    reportTableData,
    resetFilters,
    selectedActivityKeys,
    selectedGradeNames,
    toggleActivityFilter,
    toggleGradeFilter,
  ])

  return (
    <PageWrapper>
      {isFetchingSchool && <ReportsLoading background />}
      {!isFetchingSchool && (
        <>
          <ContentWrapper>
            <ReportWrapper>
              <ReportsPageHeader
                administratorsCount={administratorsCount}
                entry={entry}
                gradeOrSiteName={siteName}
                hasClassesWithCompletedLessonLastPeriod={
                  hasClassesWithCompletedLessonLastPeriod
                }
                isSchoolSummary
                k8ReportUnitSection={k8ReportUnitSection}
                lastUpdatedAt={lastUpdatedAt}
                navigationHandler={navigationHandler}
                numClassesWithCompletedLessonLastPeriod={
                  numClassesWithCompletedLessonLastPeriod
                }
                period={period}
                teachersCount={teachersCount}
                tooltipText={gradeHeaderTooltip}
                totalClassesCount={totalClassesCount}
              />
              <ReportTableV2Section>
                <ReportTableV2
                  {...filteredTablePayload}
                  handleRowClick={handleRowClick}
                  onUnitRangeChange={handleUnitRangeChange}
                />
              </ReportTableV2Section>
            </ReportWrapper>
            <Footer>
              <FooterTableAlignSection>
                <FooterTableAlignCard>
                  <BodyCopy>{footerText}</BodyCopy>
                </FooterTableAlignCard>
              </FooterTableAlignSection>
            </Footer>
          </ContentWrapper>
        </>
      )}
      <DrawerComponent
        direction="right"
        handleCloseDrawer={handleCloseDrawer}
        handleScroll={handleDrawerScroll}
        isOpen={isDrawerOpen}
        ref={closeButtonRef}
      >
        <K8Class
          HeaderComponent={ReportsClassHeader}
          classLessonColTooltip={classLessonColTooltip}
          classMarkedDoneTooltip={classMarkedDoneTooltip}
          classStatusColTooltip={classStatusColTooltip}
          filterOutUnit5={!isBHUSelected}
          fixedTop={fixedTop}
          fixedView={fixedView}
          headerProps={{
            bhuLessons: isBHUSelected ? bhuLessonsList : [],
            classTitle: classDetailsReport?.className || '',
            fixedTop,
            fixedView,
            gradeName:
              selectedClassFromTable?.gradeName ||
              classDetailsReport?.gradeName ||
              '',
            hasBHU: isBHUSelected,
            lessonCount: isBHUSelected
              ? bhuLessonsList.length
              : coreLessons?.length || 0,
            lessonsCompleted: isBHUSelected
              ? bhuLessonsList.filter(l => l.completedDate !== null).length
              : coreLessons?.filter(l => l.completedDate !== null).length || 0,
            ...(isSpecializedTab && {
              primaryProgressSectionTitle: DIGITAL_WELL_BEING_UNIT_LABEL,
            }),
            teacherFirstName: classDetailsReport?.teacherFirstName || '',
            teacherLastName: classDetailsReport?.teacherLastName || '',
          }}
          onlyBhuLessons={isBHUSelected}
        />
      </DrawerComponent>
    </PageWrapper>
  )
}

SchoolSummary.propTypes = {
  administratorsCount: PropTypes.number,
  classDetailsReport: PropTypes.object,
  entry: PropTypes.object,
  handleUnitRangeChange: PropTypes.func,
  hasClassesWithCompletedLessonLastPeriod: PropTypes.bool,
  isFetchingSchool: PropTypes.bool,
  k8ReportUnitSection: PropTypes.string,
  lastUpdatedAt: PropTypes.string,
  navigationHandler: PropTypes.func,
  numClassesWithCompletedLessonLastPeriod: PropTypes.number,
  period: PropTypes.number,
  reportTableData: PropTypes.object,
  resetFilters: PropTypes.func,
  selectedActivityKeys: PropTypes.object,
  selectedClassInstance: PropTypes.string,
  selectedGradeNames: PropTypes.object,
  selectedSite: PropTypes.number,
  selectedUnitRange: PropTypes.string,
  siteName: PropTypes.string,
  teachersCount: PropTypes.number,
  toggleActivityFilter: PropTypes.func,
  toggleGradeFilter: PropTypes.func,
  totalClassesCount: PropTypes.number,
}

export default SchoolSummary
